/**
 * Turning a pasted video link into something an <iframe> can point at.
 *
 * Provider-explicit on purpose — we only ever point an iframe at hosts we
 * trust, and an unrecognised host resolves to null rather than being embedded
 * on a hunch.
 *
 * Lives in shared rather than the dashboard because two surfaces now need the
 * same answer: community media (`MediaView`, which is where this logic was
 * born) renders whatever a post carries, and a doer's intro video is validated
 * on the server before it is stored. A second copy of the host table is how the
 * two would come to disagree about what is embeddable.
 */
import { webUrlSchema } from './web-urls.js';
/**
 * Which provider this URL belongs to and how it embeds, or null for a host we
 * will not embed.
 *
 * The embed URL is derived here every time rather than stored anywhere: it is
 * a pure function of the link, and freezing today's embed form into a database
 * column would make a provider changing theirs into a data migration.
 */
export function parseVideoLink(raw) {
    if (!raw)
        return null;
    let u;
    try {
        u = new URL(raw);
    }
    catch {
        return null;
    }
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
        if (u.pathname === '/watch') {
            const id = u.searchParams.get('v');
            return id ? youtube(id) : null;
        }
        if (u.pathname.startsWith('/embed/')) {
            return { provider: 'youtube', embedUrl: `https://www.youtube-nocookie.com${u.pathname}` };
        }
        if (u.pathname.startsWith('/shorts/')) {
            const id = u.pathname.split('/')[2];
            return id ? youtube(id) : null;
        }
        return null;
    }
    if (host === 'youtu.be') {
        const id = u.pathname.slice(1);
        return id ? youtube(id) : null;
    }
    if (host === 'vimeo.com') {
        const id = u.pathname.split('/').filter(Boolean)[0];
        return id && /^\d+$/.test(id)
            ? { provider: 'vimeo', embedUrl: `https://player.vimeo.com/video/${id}` }
            : null;
    }
    if (host === 'player.vimeo.com')
        return { provider: 'vimeo', embedUrl: u.toString() };
    // Workspace subdomains (`acme.loom.com/share/…`) are what a Loom link copied
    // from a team workspace actually looks like, and they are the common case for
    // a doer recording an intro. Matching `loom.com` exactly rejected all of them.
    if (host === 'loom.com' || host.endsWith('.loom.com')) {
        const parts = u.pathname.split('/').filter(Boolean);
        if (parts[0] === 'embed')
            return { provider: 'loom', embedUrl: u.toString() };
        if (parts[0] === 'share' && parts[1]) {
            return { provider: 'loom', embedUrl: `https://www.loom.com/embed/${parts[1]}` };
        }
        return null;
    }
    if (host.endsWith('wistia.com') || host.endsWith('wistia.net')) {
        const parts = u.pathname.split('/').filter(Boolean);
        const id = parts[parts.length - 1];
        return id ? { provider: 'wistia', embedUrl: `https://fast.wistia.net/embed/iframe/${id}` } : null;
    }
    return null;
}
function youtube(id) {
    return { provider: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}` };
}
/**
 * Map a known media URL to an embeddable iframe src, or null when it is not
 * from a provider we can safely embed (the caller falls back to a player or a
 * clickable link).
 */
export function videoEmbedUrl(raw) {
    return parseVideoLink(raw)?.embedUrl ?? null;
}
/**
 * A pasted video link: an http(s) URL from a provider we can embed.
 *
 * Composed with `webUrlSchema` rather than relying on the host table alone, so
 * `javascript:` and friends are rejected by the scheme rule that already exists
 * instead of incidentally, by failing to match a hostname.
 */
export const videoLinkSchema = webUrlSchema.refine((value) => parseVideoLink(value) !== null, {
    message: 'Paste a YouTube, Vimeo, Loom or Wistia link',
});
//# sourceMappingURL=video-links.js.map